import { type FieldValues, type Path, type UseFormReturn } from 'react-hook-form';
import { Label } from '@/components/ui/label';
import { DecimalInput } from '@/components/forms/DecimalInput';
import { cn } from '@/lib/cn';

interface FormCurrencyInputProps<T extends FieldValues> {
  form: UseFormReturn<T>;
  name: Path<T>;
  label: string;
  currency?: string;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  required?: boolean;
}

export function FormCurrencyInput<T extends FieldValues>({
  form,
  name,
  label,
  currency = 'USD',
  placeholder = '0.00',
  className,
  disabled,
  required,
}: FormCurrencyInputProps<T>) {
  const error = form.formState.errors[name];
  const currencySymbols: Record<string, string> = {
    USD: '$',
    EUR: '\u20AC',
    GBP: '\u00A3',
    INR: '\u20B9',
    CAD: 'C$',
    AUD: 'A$',
  };

  return (
    <div className={cn('space-y-2', className)}>
      <Label htmlFor={name}>
        {label}
        {required && <span className="text-destructive ml-1">*</span>}
      </Label>
      <div className="relative">
        <span className="absolute left-3 top-2.5 text-sm text-muted-foreground">
          {currencySymbols[currency] || '$'}
        </span>
        <DecimalInput
          id={name}
          placeholder={placeholder}
          disabled={disabled}
          className="pl-7"
          {...form.register(name)}
        />
      </div>
      {error && (
        <p className="text-xs text-destructive">{error.message as string}</p>
      )}
    </div>
  );
}
